import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { Tile, Loading, InlineNotification, TextInput, Button } from '@carbon/react';
import { Temperature, Humidity, Locked } from '@carbon/icons-react';
import { useTheme } from '../context/ThemeContext';
import { supabase } from '../services/supabaseClient';
import SensorChart from '../components/charts/SensorChart';
import {
  REPORT_CHART_COLORS,
  inferReportTimeRange,
  buildReportChartSeries,
  exportReportChartCsv,
} from '../utils/reportChartUtils';

const fetchPublicReportReadings = async (publicId, accessCode) => {
  const params = new URLSearchParams({ public_id: publicId });
  if (accessCode) {
    params.set('access_code', accessCode.trim().toUpperCase());
  }

  const response = await fetch(`/.netlify/functions/get-public-verify-report?${params.toString()}`);
  const payload = await response.json();

  if (!response.ok) {
    const error = new Error(payload.error || 'Failed to load report data');
    error.requiresAccessCode = payload.requires_access_code;
    throw error;
  }

  return payload.readings || [];
};

// Simple QR Code component using an <img> tag for reliable rendering
const QRCodeDisplay = ({ value, size = 200 }) => {
  if (!value) {
    return (
      <div
        style={{
          width: size,
          height: size,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: 8,
          border: '1px dashed #8d8d8d',
          fontSize: '0.75rem',
          color: '#8d8d8d',
        }}
      >
        QR code will appear here
      </div>
    );
  }

  const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=${size}x${size}&data=${encodeURIComponent(
    value
  )}`;

  return (
    <img
      src={qrUrl}
      alt="Report QR code"
      style={{
        width: size,
        height: size,
        maxWidth: '100%',
        borderRadius: 8,
        backgroundColor: '#ffffff',
        boxShadow: '0 0 0 1px rgba(0,0,0,0.06)',
      }}
    />
  );
};

const PublicReportPage = () => {
  const { publicId } = useParams();
  const { theme } = useTheme();
  
  const isDark = theme === 'dark';
  const bgPrimary = isDark ? '#161616' : '#ffffff';
  const bgSecondary = isDark ? '#262626' : '#f4f4f4';
  const textPrimary = isDark ? '#f4f4f4' : '#161616';
  const textSecondary = isDark ? '#c6c6c6' : '#525252';
  const borderColor = isDark ? '#393939' : '#e0e0e0';

  const [report, setReport] = useState(null);
  const [reportData, setReportData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [accessCode, setAccessCode] = useState('');
  const [codeError, setCodeError] = useState(null);
  const [isCodeVerified, setIsCodeVerified] = useState(false);

  // Log visit (only when code is verified or no code required)
  useEffect(() => {
    const logVisit = async () => {
      if (!report || !isCodeVerified) return;

      try {
        // Get visitor info
        const visitorInfo = {
          ip_address: null, // Will be set by backend if needed
          user_agent: navigator.userAgent,
          referrer: document.referrer,
        };

        await supabase
          .from('verify_report_visits')
          .insert([{
            report_id: report.id,
            visited_at: new Date().toISOString(),
            ip_address: visitorInfo.ip_address,
            user_agent: visitorInfo.user_agent,
            referrer: visitorInfo.referrer,
          }]);

        // Update visit count
        const { data: currentReport } = await supabase
          .from('verify_reports')
          .select('visit_count')
          .eq('id', report.id)
          .single();

        if (currentReport) {
          await supabase
            .from('verify_reports')
            .update({ visit_count: (currentReport.visit_count || 0) + 1 })
            .eq('id', report.id);
        }
      } catch (err) {
        console.error('Error logging visit:', err);
        // Don't show error to user - visit logging is non-critical
      }
    };

    if (report && isCodeVerified) {
      logVisit();
    }
  }, [report, isCodeVerified]);

  // Fetch report by public_id
  useEffect(() => {
    const fetchReport = async () => {
      try {
        setIsLoading(true);
        setError(null);

        const { data, error: fetchError } = await supabase
          .from('verify_reports')
          .select('*')
          .eq('public_id', publicId)
          .single();

        if (fetchError) throw fetchError;
        if (!data) throw new Error('Report not found');

        setReport(data);

        // If report has access code, don't verify automatically
        if (data.access_code) {
          setIsCodeVerified(false);
        } else {
          setIsCodeVerified(true);
          if (data.sensor_id && data.start_date && data.end_date) {
            const readings = await fetchPublicReportReadings(publicId);
            setReportData(readings);
          }
        }
      } catch (err) {
        console.error('Error fetching report:', err);
        setError(err.message || 'Failed to load report');
      } finally {
        setIsLoading(false);
      }
    };

    if (publicId) {
      fetchReport();
    }
  }, [publicId]);

  // Fetch chart data via server function (bypasses sensor_readings RLS for anonymous viewers)
  useEffect(() => {
    const fetchReportData = async () => {
      if (!report || !isCodeVerified || reportData || !publicId) return;

      try {
        if (report.sensor_id && report.start_date && report.end_date) {
          const readings = await fetchPublicReportReadings(
            publicId,
            report.access_code ? accessCode : null
          );
          setReportData(readings);
        }
      } catch (err) {
        console.error('Error fetching report data:', err);
        if (!err.requiresAccessCode) {
          setError(err.message || 'Failed to load chart data');
        }
      }
    };

    fetchReportData();
  }, [report, isCodeVerified, reportData, publicId, accessCode]);

  // Handle access code verification
  const handleVerifyCode = async () => {
    if (!accessCode.trim()) {
      setCodeError('Please enter an access code');
      return;
    }

    if (!report?.access_code) return;

    try {
      setCodeError(null);
      const readings = await fetchPublicReportReadings(publicId, accessCode);
      setReportData(readings);
      setIsCodeVerified(true);
    } catch (err) {
      setCodeError(
        err.requiresAccessCode
          ? 'Invalid access code. Please try again.'
          : (err.message || 'Failed to verify access code')
      );
    }
  };

  // Prepare chart data
  const chartData = useMemo(
    () => buildReportChartSeries(reportData),
    [reportData]
  );

  const reportTimeRange = useMemo(
    () => inferReportTimeRange(report?.start_date, report?.end_date),
    [report?.start_date, report?.end_date]
  );

  if (isLoading) {
    return (
      <div style={{ backgroundColor: bgPrimary, minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Loading description="Loading report..." />
      </div>
    );
  }

  if (error || !report) {
    return (
      <div style={{ backgroundColor: bgPrimary, minHeight: '100vh', padding: '24px' }}>
        <InlineNotification
          kind="error"
          title="Error"
          subtitle={error || 'Report not found'}
        />
      </div>
    );
  }

  // Show code entry form if access code is required and not verified
  if (report.access_code && !isCodeVerified) {
    return (
      <div style={{ backgroundColor: bgPrimary, minHeight: '100vh', padding: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ maxWidth: '500px', width: '100%' }}>
          <Tile style={{ backgroundColor: bgSecondary, border: `1px solid ${borderColor}`, padding: '24px 16px' }}>
            <div style={{ textAlign: 'center', marginBottom: '24px' }}>
              <Locked size={48} style={{ color: textSecondary, marginBottom: '16px' }} />
              <h1 style={{ color: textPrimary, marginBottom: '8px', fontSize: 'clamp(1.25rem, 4vw, 1.75rem)' }}>Access Code Required</h1>
              <p style={{ color: textSecondary, fontSize: '0.875rem' }}>
                This report is protected. Please enter the access code to view it.
              </p>
            </div>

            {codeError && (
              <InlineNotification
                kind="error"
                title="Invalid Code"
                subtitle={codeError}
                style={{ marginBottom: '24px' }}
              />
            )}

            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <TextInput
                id="access-code-input"
                labelText="Access Code"
                placeholder="Enter 6-character code"
                value={accessCode}
                onChange={(e) => {
                  setAccessCode(e.target.value.toUpperCase());
                  setCodeError(null);
                }}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    handleVerifyCode();
                  }
                }}
                style={{ fontFamily: 'monospace', fontWeight: 'bold', letterSpacing: '2px', textTransform: 'uppercase' }}
                maxLength={6}
              />
              <Button
                kind="primary"
                onClick={handleVerifyCode}
                style={{ width: '100%' }}
              >
                View Report
              </Button>
            </div>
          </Tile>
        </div>
      </div>
    );
  }

  return (
    <div style={{ backgroundColor: bgPrimary, minHeight: '100vh', padding: '16px 0' }}>
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 16px' }}>
        <Tile style={{ backgroundColor: bgSecondary, border: `1px solid ${borderColor}`, padding: '16px', marginBottom: '16px' }}>
          <h1 style={{ color: textPrimary, marginBottom: '8px', fontSize: 'clamp(1.25rem, 4vw, 1.75rem)' }}>Sensor Data Report</h1>
          <p style={{ color: textSecondary, fontSize: '0.875rem' }}>
            {new Date(report.start_date).toLocaleDateString()} - {new Date(report.end_date).toLocaleDateString()}
          </p>
          {report.description && (
            <p style={{ color: textSecondary, fontSize: '0.875rem', marginTop: '8px', wordBreak: 'break-word' }}>
              {report.description}
            </p>
          )}
        </Tile>

        {/* Charts */}
        {report.has_temperature && chartData.tempData.length > 0 && (
          <Tile style={{ backgroundColor: bgSecondary, border: `1px solid ${borderColor}`, padding: '16px', marginBottom: '16px' }}>
            <h2 style={{ color: textPrimary, marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px', fontSize: 'clamp(1rem, 4vw, 1.25rem)' }}>
              <Temperature size={20} /> Temperature
            </h2>
            <SensorChart
              data={chartData.tempData}
              label="Temperature"
              unit="°C"
              color={REPORT_CHART_COLORS.temperature}
              timeRange={reportTimeRange}
              isDark={isDark}
              onExport={(dataType) => exportReportChartCsv(chartData.tempData, dataType, `report-${publicId}`)}
              emptyLabel="No temperature data available for the selected date range"
            />
          </Tile>
        )}

        {report.has_humidity && chartData.humidityData.length > 0 && (
          <Tile style={{ backgroundColor: bgSecondary, border: `1px solid ${borderColor}`, padding: '16px' }}>
            <h2 style={{ color: textPrimary, marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px', fontSize: 'clamp(1rem, 4vw, 1.25rem)' }}>
              <Humidity size={20} /> Humidity
            </h2>
            <SensorChart
              data={chartData.humidityData}
              label="Humidity"
              unit="%"
              color={REPORT_CHART_COLORS.humidity}
              timeRange={reportTimeRange}
              isDark={isDark}
              onExport={(dataType) => exportReportChartCsv(chartData.humidityData, dataType, `report-${publicId}`)}
              emptyLabel="No humidity data available for the selected date range"
            />
          </Tile>
        )}

        {(!report.has_temperature || chartData.tempData.length === 0) && 
         (!report.has_humidity || chartData.humidityData.length === 0) && (
          <Tile style={{ backgroundColor: bgSecondary, border: `1px solid ${borderColor}`, padding: '24px' }}>
            <p style={{ color: textSecondary }}>No data available for the selected date range.</p>
          </Tile>
        )}
      </div>
    </div>
  );
};

export default PublicReportPage;
